import * as THREE from 'three'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js'
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js'
import { layoutText, wrapText } from './pixelFont'
import type { Cell } from './games/types'

export interface GridOptions {
	/** Blocks rain in from above (used for text, not game frames). */
	drop?: boolean
	/** Gentle per-column bob. */
	wobble?: boolean
	/** Pointer parallax / drag spin. Off for games so the board faces you. */
	spin?: boolean
}

const CAM_D = 11
const MAX_SCALE = 0.19

/**
 * The synthwave cabinet: scrolling neon grid, striped sun, mountain silhouettes and the
 * message rendered as glowing voxels (with a floor reflection). Imperative on purpose;
 * React only tells it what text to show.
 */
export class ArcadeScene {
	private renderer: THREE.WebGLRenderer
	private scene = new THREE.Scene()
	private camera = new THREE.PerspectiveCamera(55, 1, 0.1, 200)
	private composer: EffectComposer
	private grid: THREE.Mesh<THREE.PlaneGeometry, THREE.ShaderMaterial>
	private sun: THREE.Mesh<THREE.CircleGeometry, THREE.ShaderMaterial>
	private cube = new THREE.BoxGeometry(0.86, 0.86, 0.86)
	private text: THREE.InstancedMesh | null = null
	private mirror: THREE.InstancedMesh | null = null
	private capacity = 0
	private cells: Cell[] = []
	private opts: Required<GridOptions> = { drop: true, wobble: true, spin: true }
	private gridMode = false
	private W = 1
	private H = 7
	private sc = MAX_SCALE
	private spin = 0
	private targetSpin = 0
	private dragging = false
	private lastX = 0
	private pointerX = 0.5
	private attract = false
	private god = false
	private textSince = 0
	private raf = 0
	private t0 = performance.now()
	private reduce = matchMedia('(prefers-reduced-motion: reduce)').matches
	private colour = new THREE.Color()
	private M = new THREE.Matrix4()
	private Q = new THREE.Quaternion()
	private S = new THREE.Vector3()
	private P = new THREE.Vector3()
	private E = new THREE.Euler()
	private currentText = ''

	constructor(private canvas: HTMLCanvasElement) {
		this.renderer = new THREE.WebGLRenderer({ canvas, antialias: false, powerPreference: 'high-performance' })
		this.renderer.setPixelRatio(Math.min(devicePixelRatio, 2))
		this.scene.fog = new THREE.Fog(0x0b0220, 18, 70)
		this.buildSky()
		this.sun = this.buildSun()
		this.scene.add(this.ridge(-88, 22, 0x1a0640, 7), this.ridge(-70, 14, 0x0e0328, 3))
		this.grid = this.buildFloor()
		this.composer = new EffectComposer(this.renderer)
		this.composer.addPass(new RenderPass(this.scene, this.camera))
		this.composer.addPass(new UnrealBloomPass(new THREE.Vector2(Math.max(1, innerWidth), Math.max(1, innerHeight)), 1.1, 0.55, 0.55))
		this.resize()
		addEventListener('resize', this.resize)
		canvas.addEventListener('pointerdown', this.onDown)
		addEventListener('pointerup', this.onUp)
		addEventListener('pointermove', this.onMove)
		this.raf = requestAnimationFrame(this.frame)
	}

	/** Show a new message. Long strings wrap on dots/colons to suit the viewport. */
	setText(text: string): void {
		this.currentText = text
		this.gridMode = false
		const maxChars = Math.max(7, Math.min(40, Math.round(this.camera.aspect * 15)))
		const layout = layoutText(wrapText(text, maxChars))
		const colour = new THREE.Color()
		const cells: Cell[] = layout.cells.map(([x, y]) => {
			colour.setHSL((0.83 - (x / layout.width) * 0.33 + 1) % 1, 1, 0.62)
			return { x, y, color: colour.getHex() }
		})
		this.applyGrid(cells, layout.width, layout.height, { drop: true, wobble: true, spin: true })
	}

	/** Show an arbitrary cell grid (a game frame). Cheap enough to call every tick. */
	setGrid(cells: Cell[], width: number, height: number, opts: GridOptions = {}): void {
		this.gridMode = true
		this.applyGrid(cells, width, height, { drop: false, wobble: false, spin: false, ...opts })
	}

	/** Take the voxels off the board (a game is playing on the CRT instead). */
	clear(): void {
		this.currentText = ''
		this.gridMode = false
		this.applyGrid([], 1, 7, { drop: false, wobble: false, spin: true })
	}

	private applyGrid(cells: Cell[], width: number, height: number, opts: Required<GridOptions>): void {
		const sameSize = this.W === width && this.H === height
		this.cells = cells
		this.W = width
		this.H = height
		this.opts = opts
		this.ensureCapacity(cells.length)
		this.text!.count = cells.length
		this.mirror!.count = cells.length
		this.paint(0)
		if (!sameSize || !opts.drop) this.fit()
		if (opts.drop) this.textSince = performance.now()
		else this.textSince = 0
	}

	private ensureCapacity(n: number): void {
		if (this.text && n <= this.capacity) return
		if (this.text) {
			this.scene.remove(this.text, this.mirror!)
			this.text.dispose()
			this.mirror!.dispose()
		}
		this.capacity = Math.max(1024, n)
		this.text = new THREE.InstancedMesh(this.cube, new THREE.MeshBasicMaterial({ color: 0xffffff, fog: false }), this.capacity)
		this.mirror = new THREE.InstancedMesh(
			this.cube,
			new THREE.MeshBasicMaterial({ color: 0xffffff, fog: false, transparent: true, opacity: 0.22, depthTest: false }),
			this.capacity,
		)
		this.mirror.renderOrder = 1
		// instanceColor is only created by setColorAt; seed it so needsUpdate works on the first paint
		const white = new THREE.Color(0xffffff)
		this.text.setColorAt(0, white)
		this.mirror.setColorAt(0, white)
		this.text.frustumCulled = false
		this.mirror.frustumCulled = false
		this.scene.add(this.text, this.mirror)
	}

	setAttract(on: boolean): void {
		this.attract = on
	}

	setGodMode(on: boolean): void {
		this.god = on
	}

	dispose(): void {
		cancelAnimationFrame(this.raf)
		removeEventListener('resize', this.resize)
		this.canvas.removeEventListener('pointerdown', this.onDown)
		removeEventListener('pointerup', this.onUp)
		removeEventListener('pointermove', this.onMove)
		this.renderer.dispose()
	}

	// ---- construction ----

	private buildSky(): void {
		const m = new THREE.ShaderMaterial({
			depthWrite: false,
			vertexShader: `varying vec2 vUv;void main(){vUv=uv;gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.);}`,
			fragmentShader: `varying vec2 vUv;float h(vec2 p){return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453);}
			void main(){vec3 top=vec3(0.043,0.008,0.125);vec3 bot=vec3(0.36,0.05,0.42);vec3 c=mix(bot,top,smoothstep(0.35,1.0,vUv.y));
			vec2 g=floor(vUv*vec2(420.,240.));float s=step(0.996,h(g));c+=s*vec3(0.9,0.85,1.0)*(0.5+0.5*h(g+1.));gl_FragColor=vec4(c,1.);}`,
		})
		const sky = new THREE.Mesh(new THREE.PlaneGeometry(400, 200), m)
		sky.position.set(0, 40, -120)
		this.scene.add(sky)
	}

	private buildSun(): THREE.Mesh<THREE.CircleGeometry, THREE.ShaderMaterial> {
		const m = new THREE.ShaderMaterial({
			transparent: true,
			uniforms: { t: { value: 0 } },
			vertexShader: `varying vec2 vUv;void main(){vUv=uv;gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.);}`,
			fragmentShader: `varying vec2 vUv;uniform float t;void main(){vec2 p=vUv-0.5;float d=length(p);if(d>0.5)discard;
			float y=vUv.y;vec3 c=mix(vec3(1.0,0.17,0.65),vec3(1.0,0.85,0.29),smoothstep(0.1,0.9,y));
			if(y<0.5&&fract(y*14.0-t*0.6)<0.1+(0.5-y)*1.2)discard;
			float edge=smoothstep(0.5,0.46,d);gl_FragColor=vec4(c*1.6,edge);}`,
		})
		const sun = new THREE.Mesh(new THREE.CircleGeometry(16, 64), m)
		sun.position.set(0, 12, -90)
		this.scene.add(sun)
		return sun
	}

	private ridge(z: number, h: number, colour: number, seed: number): THREE.Mesh {
		const pts: THREE.Vector2[] = []
		const w = 260
		const n = 70
		let s = seed
		const rnd = () => ((s = (s * 9301 + 49297) % 233280), s / 233280)
		pts.push(new THREE.Vector2(-w / 2, 0))
		for (let i = 0; i <= n; i++) {
			const x = -w / 2 + (i / n) * w
			const y = (0.35 + rnd() * 0.65) * h * (1 - Math.min(1, Math.abs(x) / (w / 2)) * 0.2) * (Math.abs(x) < 26 ? 0.35 : 1)
			pts.push(new THREE.Vector2(x, y))
		}
		pts.push(new THREE.Vector2(w / 2, 0))
		const mesh = new THREE.Mesh(new THREE.ShapeGeometry(new THREE.Shape(pts)), new THREE.MeshBasicMaterial({ color: colour }))
		mesh.position.set(0, 0, z)
		return mesh
	}

	private buildFloor(): THREE.Mesh<THREE.PlaneGeometry, THREE.ShaderMaterial> {
		const floor = new THREE.Mesh(new THREE.PlaneGeometry(200, 140), new THREE.MeshBasicMaterial({ color: 0x07001a }))
		floor.rotation.x = -Math.PI / 2
		floor.position.set(0, -0.01, -40)
		this.scene.add(floor)
		const grid = new THREE.Mesh(
			new THREE.PlaneGeometry(200, 140),
			new THREE.ShaderMaterial({
				transparent: true,
				depthWrite: false,
				depthTest: false,
				blending: THREE.AdditiveBlending,
				uniforms: { t: { value: 0 } },
				vertexShader: `varying vec2 vUv;void main(){vUv=uv;gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.);}`,
				fragmentShader: `varying vec2 vUv;uniform float t;void main(){vec2 p=vUv*vec2(50.0,35.0);p.y-=t*1.4;
				vec2 g=abs(fract(p-0.5)-0.5)/fwidth(p);float l=1.0-min(min(g.x,g.y),1.0);
				float fade=smoothstep(1.0,0.25,vUv.y);vec3 c=mix(vec3(1.0,0.17,0.84),vec3(0.13,0.94,1.0),vUv.y)*l*2.2;
				gl_FragColor=vec4(c*fade,1.0);}`,
			}),
		)
		grid.rotation.x = -Math.PI / 2
		grid.position.set(0, 0, -40)
		grid.renderOrder = 2
		this.scene.add(grid)
		return grid
	}

	// ---- layout ----

	private fit(): void {
		const visW = 2 * Math.tan((this.camera.fov / 2) * (Math.PI / 180)) * this.camera.aspect * CAM_D
		this.sc = Math.min(MAX_SCALE, (visW * 0.9) / this.W, 4.2 / this.H)
		this.camera.position.set(0, 5.0, CAM_D)
		this.camera.lookAt(0, 3.4, 0)
	}

	private paint(hueShift: number): void {
		if (!this.text || !this.mirror) return
		for (let i = 0; i < this.cells.length; i++) {
			this.colour.setHex(this.cells[i].color)
			if (hueShift) this.colour.offsetHSL(hueShift, 0, 0)
			this.text.setColorAt(i, this.colour)
			this.mirror.setColorAt(i, this.colour)
		}
		this.text.instanceColor!.needsUpdate = true
		this.mirror.instanceColor!.needsUpdate = true
	}

	private resize = (): void => {
		const w = Math.max(1, innerWidth)
		const h = Math.max(1, innerHeight)
		this.renderer.setSize(w, h, false)
		this.composer.setSize(w, h)
		this.camera.aspect = w / h
		this.camera.updateProjectionMatrix()
		if (this.gridMode) this.fit()
		else if (this.currentText) this.setText(this.currentText)
		else this.fit()
	}

	// ---- input ----

	private onDown = (e: PointerEvent): void => {
		if (!this.opts.spin) return
		this.dragging = true
		this.lastX = e.clientX
	}
	private onUp = (): void => {
		this.dragging = false
	}
	private onMove = (e: PointerEvent): void => {
		this.pointerX = e.clientX / innerWidth
		if (this.dragging) {
			this.targetSpin += (e.clientX - this.lastX) * 0.01
			this.lastX = e.clientX
		}
	}

	// ---- loop ----

	private frame = (now: number): void => {
		this.raf = requestAnimationFrame(this.frame)
		const t = (now - this.t0) / 1000
		const anim = this.reduce ? 0 : t
		this.grid.material.uniforms.t.value = anim
		this.sun.material.uniforms.t.value = anim
		if (!this.opts.spin) this.targetSpin = 0
		else if (this.attract) this.targetSpin += 0.01
		else if (!this.dragging) this.targetSpin = (this.pointerX - 0.5) * 0.5
		this.spin += (this.targetSpin - this.spin) * 0.08
		if (this.god && !this.reduce) this.paint((t * 0.25) % 1)

		if (this.text && this.mirror) {
			const k = this.opts.drop && this.textSince ? Math.min(1, (now - this.textSince) / 1600) : 1
			const drop = (1 - k) * (1 - k) * 22
			const cy0 = 3.4 + (this.H * this.sc) / 2 // top of the block
			this.E.set(0, this.spin, 0)
			this.Q.setFromEuler(this.E)
			this.S.setScalar(this.sc)
			for (let i = 0; i < this.cells.length; i++) {
				const { x: cx, y: ry } = this.cells[i]
				const wob = this.opts.wobble && !this.reduce ? Math.sin(t * 2.4 + cx * 0.35) * 0.22 : 0
				const delay = Math.max(0, drop - cx * 0.44)
				this.P.set((cx - this.W / 2) * this.sc, cy0 - ry * this.sc + (wob + delay) * this.sc, 0)
				this.P.applyQuaternion(this.Q)
				this.M.compose(this.P, this.Q, this.S)
				this.text.setMatrixAt(i, this.M)
				this.P.y = -this.P.y
				this.M.compose(this.P, this.Q, this.S)
				this.mirror.setMatrixAt(i, this.M)
			}
			this.text.instanceMatrix.needsUpdate = true
			this.mirror.instanceMatrix.needsUpdate = true
		}
		this.composer.render()
	}
}
